/**
 * A component that renders AIGuideActivity assignment customization options.
 **/
import {plural, t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";
import {useEffect, useMemo, useState} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {OptionItem, SingleSelect} from "@khanacademy/wonder-blocks-dropdown";
import {LabeledTextField} from "@khanacademy/wonder-blocks-form";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium, LabelSmall} from "@khanacademy/wonder-blocks-typography";
import {containsAIGuideActivityContent} from "@khan/assignments-utils";
import {
    InputKind,
    useConfigurableInputsForActivity,
    type ValidationOptions,
} from "@khan/guide-core";

import {AIGuideActivityKind} from "./constants.ts";

export interface AssignmentActivityCustomizationProps {
    title?: string;
    instructions?: string;
    configuredActivityInputs?: Record<string, string>;
}

interface Props {
    qualifiedContentDescriptorsList: Array<string>;
    onAssignmentUpdate: (props: AssignmentActivityCustomizationProps) => void;
    setCustomActivityAssignButtonEnabled: (enabled: boolean) => void;
}

const TITLE_MAX_CHARACTERS = 160;
const INSTRUCTIONS_MAX_CHARACTERS = 2000;

// TODO(mollyknaupka): There are many, many textKeyTo* maps in this file. We
// will need a new solution to this string management as the number of these
// assignable activities grows since the probability that two activities will
// have the same text key is high. We should consider a more general solution
// for this in the future as this prevents two activities with the same text
// key from displaying different translated texts, labels, placeholders,
// descriptions, error messages, etc.

// ConfigurableInputs defined in activity configs have IDs that need
// to be mapped to translated text in the client.
const getTextKeyToTranslatedTextMap: () => Record<string, string> = () => ({
    gradeLevel: t`Grade level`,
    "7g": t`7th grade`,
    "8g": t`8th grade`,
    "9g": t`9th grade`,
    "10g": t`10th grade`,
    "11g": t`11th grade`,
    "12g": t`12th grade`,
    essayType: t`Essay type`,
    persuasive: t`Persuasive/argumentative`,
    explanatory: t`Explanatory/expository`,
    literary: t`Literary analysis`,
    essayInstructions: t`Essay instructions`,
    minimumWordCount: t`Minimum word count`,
});

const getTextKeyToDescriptionMap: () => Record<string, string> = () => ({
    essayInstructions: t`Enter the essay instructions for your students here. Be as specific and detailed as possible in your instructions so Khanmigo can better assist your students.`,
    minimumWordCount: t`There is a maximum word count of 1750.`,
});

const getTextKeyToErrorMessageMap: () => Record<string, string> = () => ({
    essayInstructions: plural(10, {
        one: "Essay prompt/instructions should be at least # word long",
        other: "Essay prompt/instructions should be at least # words long",
    }),
});

const getTextKeyToPlaceholderTextMap: () => Record<string, string> = () => ({
    writingCoachAssignmentTitle: t`Example: Curfew for Teenagers Persuasive Essay`,
    gradeLevel: t`Select a grade level`,
    essayType: t`Select an essay type`,
    essayInstructions: t`Example: Should parents impose curfews on teenagers? Why or why not? Take a position after conducting your own research and write an essay stating your position. Have a clear introduction and use evidence from reliable sources for each side, pointing out the strengths and limitations of both sides.`,
});

const getTextKeyToAriaLabelMap: () => Record<string, string> = () => ({
    essayInstructions: t`Your custom instructions for the student`,
});

const isFieldValid = new Map<string, boolean>();

const ActivityCustomization = (props: Props): React.ReactElement | null => {
    const [title, setTitle] = useState("");
    const [instructions, setInstructions] = useState("");
    const [_, setTitleErrorMessage] = useState<string | null | undefined>();
    const [, setTextBoxErrorMessage] = useState<string | null | undefined>();
    const {
        onAssignmentUpdate,
        qualifiedContentDescriptorsList,
        setCustomActivityAssignButtonEnabled,
    } = props;
    const contentID = useMemo(() => {
        if (qualifiedContentDescriptorsList.length === 0) {
            return "";
        }

        const splitQualifiedDescriptor =
            qualifiedContentDescriptorsList[0].split(":");

        if (splitQualifiedDescriptor.length < 2) {
            return "";
        }

        if (
            splitQualifiedDescriptor[splitQualifiedDescriptor.length - 2] !==
            AIGuideActivityKind
        ) {
            return "";
        }

        return splitQualifiedDescriptor[splitQualifiedDescriptor.length - 1];
    }, [qualifiedContentDescriptorsList]);

    const configurableInputs = useConfigurableInputsForActivity(contentID);

    // These functions manage the form state for components which are
    // determined by the configurable inputs. Keys in formValues are
    // the keys of the configurable inputs, and values are either an
    // identifier for the selected value or the value itself (depending on
    // the input kind).
    const [formValues, setFormValues] = useState({});
    const handleFormChange = (inputKey, newValue) => {
        setFormValues((prevValues) => ({
            ...prevValues,
            [inputKey]: newValue,
        }));

        onAssignmentUpdate({
            configuredActivityInputs: {
                [inputKey]: newValue,
            },
        });
    };

    const requiredInputs = useMemo(() => {
        if (configurableInputs.status === "success") {
            return configurableInputs.data.filter((input) => !input.optional);
        }

        return null;
    }, [configurableInputs]);

    // Determine if the assign button should be enabled
    useEffect(() => {
        if (!title || configurableInputs.status !== "success") {
            setCustomActivityAssignButtonEnabled(false);
            return;
        }

        // If all required inputs are populated in some way, then enable
        // the assign button through a callback. Otherwise, disable the
        // button
        if (requiredInputs && requiredInputs?.length > 0) {
            const allRequiredInputsPopulated = requiredInputs.every(
                (input) => formValues[input.key],
            );

            if (allRequiredInputsPopulated && isFormValid()) {
                setCustomActivityAssignButtonEnabled(true);
            } else {
                setCustomActivityAssignButtonEnabled(false);
            }
        } else {
            if (isFormValid()) {
                setCustomActivityAssignButtonEnabled(true);
            } else {
                setCustomActivityAssignButtonEnabled(false);
            }
        }
    }, [
        requiredInputs,
        formValues,
        title,
        configurableInputs,
        setCustomActivityAssignButtonEnabled,
    ]);

    const onChangeTitle = (updatedTitle: string) => {
        setTitle(updatedTitle);
        onAssignmentUpdate({title: updatedTitle});
    };

    const onChangeInstructions = (updatedInstructions: string) => {
        setInstructions(updatedInstructions);
        onAssignmentUpdate({instructions: updatedInstructions});
    };

    const validateTitle = (value: string) => {
        if (!value) {
            isFieldValid.set("title", false);
            return t`Assignment title is required`;
        }
        if (value.length > TITLE_MAX_CHARACTERS) {
            isFieldValid.set("title", false);
            return t`Assignment title must be ${TITLE_MAX_CHARACTERS} characters or less`;
        }
        isFieldValid.set("title", true);
        return null;
    };

    const handleValidateTitle = (errorMessage?: string | null) => {
        setTitleErrorMessage(errorMessage);
    };

    const validateTextBoxInput = (
        input: string,
        message: string,
        optional: boolean,
        fieldKey: string,
        inputType: string,
        validationOptions?: ValidationOptions,
    ) => {
        if (
            (optional && (input == null || input.trim() === "")) ||
            validationOptions == null
        ) {
            isFieldValid.set(fieldKey, true);
            return null;
        }

        //Additional validation based on inputType
        if (inputType === "number") {
            return validateTextBoxNumber(
                input,
                message,
                optional,
                fieldKey,
                inputType,
                validationOptions,
            );
        }
        //TODO Handle other inputType values
        return null;
    };

    const validateTextBoxNumber = (
        input: string,
        message: string,
        optional: boolean,
        fieldKey: string,
        inputType: string,
        validationOptions: ValidationOptions,
    ) => {
        const inputNum = Number(input);
        if (
            validationOptions.minimum != null &&
            inputNum < validationOptions.minimum
        ) {
            isFieldValid.set(fieldKey, false);
            return t`Minimum word count must be a number greater than 0`;
        }

        if (
            validationOptions.maximum != null &&
            inputNum > validationOptions.maximum
        ) {
            isFieldValid.set(fieldKey, false);
            return t`Minimum word count must be a number less than 1750`;
        }
        isFieldValid.set(fieldKey, true);
        return null;
    };

    const handleValidateTextBoxInput = (errorMessage?: string | null) => {
        setTextBoxErrorMessage(errorMessage);
    };

    const hasAIGuideActivityContent = containsAIGuideActivityContent(
        qualifiedContentDescriptorsList,
    );
    const hasMultipleContent = qualifiedContentDescriptorsList.length > 1;

    // Only render these customization options if there is only one AIGuideActivity content,
    // as the current workflow only supports customizing one AIGuideActivity assignment creation at a time.
    if (hasMultipleContent || !hasAIGuideActivityContent) {
        return null;
    }

    const textKeyToTranslatedText = getTextKeyToTranslatedTextMap();
    const textKeyToDescription = getTextKeyToDescriptionMap();
    const textKeyToPlaceholderText = getTextKeyToPlaceholderTextMap();
    const textKeyToAriaLabel = getTextKeyToAriaLabelMap();
    const textKeyToErrorMessageMap = getTextKeyToErrorMessageMap();

    return (
        <View>
            {(configurableInputs.status === "error" ||
                configurableInputs.status === "aborted" ||
                configurableInputs.status === "no-data") && (
                <LabelSmall style={styles.error}>
                    {t`There was an error loading the customization options. Please try again later.`}
                </LabelSmall>
            )}
            {configurableInputs.status === "loading" && (
                <CircularSpinner size="small" />
            )}
            {configurableInputs.status === "success" && (
                <View>
                    <LabeledTextField
                        id="activity-assignment-title-input"
                        label={t`Assignment title`}
                        description={t`This required title will be shown on your students' list of assignments. Max number of characters is ${TITLE_MAX_CHARACTERS}.`}
                        placeholder=""
                        value={title}
                        onChange={onChangeTitle}
                        required={true}
                        validate={(value) => validateTitle(value)}
                        onValidate={handleValidateTitle}
                        style={styles.placeholderText}
                    />
                    <Strut size={spacing.xSmall_8} />
                    {/* If there are no configurableInputs for the activity, show
                     only the default inputs (title and instructions) */}
                    {configurableInputs.data.length === 0 && (
                        <View>
                            <LabelMedium>{t`Instructions`}</LabelMedium>
                            <Strut size={spacing.xxxSmall_4} />
                            <LabelSmall style={styles.instructionsDescription}>
                                {t`These custom instructions will provide structure for your students in their chats with Khanmigo. Max number of characters is ${INSTRUCTIONS_MAX_CHARACTERS}.`}
                            </LabelSmall>
                            <Strut size={spacing.xxxSmall_4} />
                            <Strut size={spacing.xSmall_8} />
                            <textarea
                                id="activity-assignment-instructions-input"
                                className={css(styles.textArea)}
                                aria-label="Your custom instructions for the student"
                                value={instructions}
                                onChange={(e) =>
                                    onChangeInstructions(e.target.value)
                                }
                                placeholder=""
                                maxLength={INSTRUCTIONS_MAX_CHARACTERS}
                                rows={5}
                            />
                        </View>
                    )}
                    {/* If there are configurableInputs for the activity, show
                     the configurable inputs in addition to the title */}
                    {configurableInputs.data.map((configurableInput, index) => {
                        return (
                            <View key={index}>
                                <View style={styles.headerLabel}>
                                    <LabelMedium
                                        tag="label"
                                        htmlFor={`activity-assignment-${configurableInput.key}-input`}
                                        testId="label-to-remove"
                                    >
                                        {
                                            textKeyToTranslatedText[
                                                configurableInput.key
                                            ]
                                        }
                                    </LabelMedium>
                                    <Strut size={spacing.xxxSmall_4} />
                                    {!configurableInput.optional && (
                                        <LabelSmall style={styles.required}>
                                            *
                                        </LabelSmall>
                                    )}
                                </View>

                                {textKeyToDescription[
                                    configurableInput.key
                                ] && (
                                    <View>
                                        <Strut size={spacing.xxxSmall_4} />
                                        <LabelMedium style={styles.description}>
                                            {
                                                textKeyToDescription[
                                                    configurableInput.key
                                                ]
                                            }
                                        </LabelMedium>
                                    </View>
                                )}
                                <Strut size={spacing.xSmall_8} />
                                {configurableInput.inputKind ===
                                    InputKind.singleSelect && (
                                    <SingleSelect
                                        onChange={(newOptionKey) =>
                                            handleFormChange(
                                                configurableInput.key,
                                                newOptionKey,
                                            )
                                        }
                                        id={`activity-assignment-${configurableInput.key}-input`}
                                        selectedValue={
                                            formValues[configurableInput.key]
                                        }
                                        placeholder={
                                            textKeyToPlaceholderText[
                                                configurableInput.key
                                            ]
                                        }
                                        style={styles.singleSelect}
                                    >
                                        {configurableInput.options.map(
                                            (option, index) => {
                                                return (
                                                    <OptionItem
                                                        key={index}
                                                        testId={option.key}
                                                        value={option.key}
                                                        label={
                                                            textKeyToTranslatedText[
                                                                option.key
                                                            ]
                                                        }
                                                    />
                                                );
                                            },
                                        )}
                                    </SingleSelect>
                                )}
                                {configurableInput.inputKind ===
                                    InputKind.textArea && (
                                    <textarea
                                        id={`activity-assignment-${configurableInput.key}-input`}
                                        className={css(styles.textArea)}
                                        aria-label={
                                            textKeyToAriaLabel[
                                                configurableInput.key
                                            ]
                                        }
                                        value={
                                            formValues[configurableInput.key]
                                        }
                                        onChange={(e) =>
                                            handleFormChange(
                                                configurableInput.key,
                                                e.target.value,
                                            )
                                        }
                                        placeholder={
                                            textKeyToPlaceholderText[
                                                configurableInput.key
                                            ]
                                        }
                                        maxLength={INSTRUCTIONS_MAX_CHARACTERS}
                                        rows={5}
                                    />
                                )}
                                {configurableInput.inputKind ===
                                    InputKind.textBox && (
                                    <LabeledTextField
                                        id={`activity-assignment-${configurableInput.key}-input`}
                                        style={styles.placeholderText}
                                        label=""
                                        aria-label={
                                            textKeyToAriaLabel[
                                                configurableInput.key
                                            ]
                                        }
                                        value={
                                            formValues[configurableInput.key]
                                        }
                                        onChange={(e) =>
                                            handleFormChange(
                                                configurableInput.key,
                                                e,
                                            )
                                        }
                                        placeholder={
                                            textKeyToPlaceholderText[
                                                configurableInput.key
                                            ]
                                        }
                                        type={configurableInput.inputType}
                                        validate={(text) =>
                                            validateTextBoxInput(
                                                text,
                                                textKeyToErrorMessageMap[
                                                    configurableInput.key
                                                ],
                                                !!configurableInput.optional,
                                                configurableInput.key,
                                                configurableInput.inputType,
                                                configurableInput.validationOptions,
                                            )
                                        }
                                        onValidate={handleValidateTextBoxInput}
                                    />
                                )}
                                <Strut size={spacing.small_12} />
                            </View>
                        );
                    })}
                </View>
            )}
            <Strut size={spacing.xLarge_32} />
        </View>
    );
};

function isFormValid(): boolean {
    for (const value of isFieldValid.values()) {
        if (!value) {
            return false;
        }
    }
    return true;
}

const styles = StyleSheet.create({
    placeholderText: {
        MozOsxFontSmoothing: "grayscale",
        WebkitFontSmoothing: "antialiased",
    },
    instructionsDescription: {
        color: color.offBlack64,
    },
    error: {
        color: color.red,
    },
    headerLabel: {
        flexDirection: "row",
    },
    required: {
        color: color.red,
    },
    singleSelect: {
        width: "100%",
    },
    description: {
        color: color.offBlack64,
    },
    textArea: {
        height: "100%",
        borderRadius: 4,
        resize: "none",
        border: `1px solid ${color.offBlack50}`,
        fontFamily: "Lato, sans-serif",
        fontSize: "16px !important",
        fontWeight: "normal",
        lineHeight: "20px",
        padding: "10px 16px",
        overflowY: "auto",
        MozOsxFontSmoothing: "grayscale",
        WebkitFontSmoothing: "antialiased",
    },
});

export default ActivityCustomization;
